import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "../../components/common/LoadingComponent";
import { Icons } from "../../constants/icon-library";
import {
  CATEGORY_TITLES,
  getIcon,
  PLATFORM_DISPLAY_NAMES,
} from "../../constants/constants";
import { clearAssistants } from "../../features/assistant/assistantSlice";
import { getOnboardingAssistants } from "../../features/organization/organizationSlice";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import { handlePlatformAuth } from "../AssistantDetails/Assistants.utils";
import { getAllUserMeetings } from "../../features/meetings/meetingsSlice";

function Apps() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const { onboardingGroups, loading: orgLoading } = useSelector(
    (state) => state.organization,
  );

  const { userSettings, loading: settingLoading } = useSelector(
    (state) => state.setting,
  );

  const installingRef = useRef(false);
  useEffect(() => {
    dispatch(getAllUserMeetings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearAssistants());
    if (user?.role) {
      dispatch(getOnboardingAssistants());
    }
  }, [user?.role, dispatch]);

  useEffect(() => {
    dispatch(fetchSettingDetailsByUserId());
  }, [dispatch]);

  const installedMap = useMemo(() => {
    const map = {};
    userSettings.forEach((s) => {
      if (s?.assistantId?.uuid) {
        map[s.assistantId.uuid] = s;
      }
    });
    return map;
  }, [userSettings]);

  const loading = orgLoading || settingLoading;
  const connectedCount = Object.keys(installedMap).length;

  const handleInstall = async (assistant) => {
    if (installingRef.current) return;
    installingRef.current = true;

    try {
      await handlePlatformAuth({
        assistant,
        settingData: null,
        dispatch,
        updateSetting: () => {},
        user,
        isOnboarding: true,
      });
    } catch (error) {
      console.error("Install failed:", error);
      installingRef.current = false;
    }
  };

  const handleRedirect = () => {
    navigate("/onboarding/plans");
  };

  return (
    <div className="container py-5 mt-4" style={{ maxWidth: "1400px" }}>
      <div className="text-center mb-7">
        <h2 className="fw-bold">
          Connect your Project Management, Communication tools you use
        </h2>
        <p className="text-muted">
          Link your platforms to seamlessly integrate with Project Management,
          Email, Communication, Todo list, etc platforms you use and let Gennie
          perform actions directly within your meetings.
        </p>
      </div>

      {loading ? (
        <LoadingComponent loading={true} size={15} />
      ) : (
        <>
          {Object.entries(onboardingGroups || {}).map(
            ([category, assistants]) => {
              if (!assistants?.length) return null;

              return (
                <div key={category} className="mb-4">
                  <h4 className="fw-bold mb-3">{CATEGORY_TITLES[category]}</h4>

                  <div className="row g-3">
                    {assistants.map((assistant) => {
                      const installedSetting = installedMap[assistant.uuid];

                      const platformName =
                        assistant?.platform?.type?.toLowerCase();

                      const iconSrc =
                        getIcon(platformName) || assistant?.platform?.iconUrl;

                      return (
                        <div
                          key={assistant.uuid}
                          className="col-lg-3 col-md-4 col-sm-6"
                        >
                          <div
                            className="card h-100 text-center shadow-sm p-3"
                            style={{
                              borderRadius: "18px",
                              minHeight: "220px",
                            }}
                          >
                            <div className="card-body d-flex flex-column align-items-center justify-content-center">
                              <div
                                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-3"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                }}
                              >
                                <img
                                  src={iconSrc}
                                  alt={assistant.name}
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                  }}
                                />
                              </div>

                              <h5 className="fw-semibold mb-3">
                                {PLATFORM_DISPLAY_NAMES[platformName] ||
                                  assistant.name}
                              </h5>

                              {installedSetting ? (
                                <button className="btn btn-success w-100">
                                  <i className={`wb ${Icons("check")} me-1`} />
                                  Connected
                                </button>
                              ) : (
                                <button
                                  className="btn btn-primary w-100"
                                  onClick={() => handleInstall(assistant)}
                                >
                                  <i
                                    className={`wb ${Icons("install")} me-1`}
                                  />
                                  Install
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            },
          )}
        </>
      )}

      <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
        <button
          className="btn btn-primary px-4"
          onClick={() => navigate("/onboarding/meetings")}
        >
          ← Previous
        </button>

        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-link text-muted"
            onClick={handleRedirect}
            style={{ fontSize: "1.1rem" }}
          >
            Skip for now
          </button>

          <button
            className="btn btn-primary px-4"
            disabled={connectedCount === 0}
            onClick={handleRedirect}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

export default Apps;
